import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  MoreVertical,
  Mail,
  Trash2,
  Send,
  XCircle,
  RefreshCw,
  StickyNote,
  Copy,
  Clock,
  CheckCircle2,
  Hourglass,
  Ban,
} from 'lucide-react';

const STATUS_META = {
  pending:    { label: { fr: 'En attente',  en: 'Pending'    }, variant: 'secondary',   icon: Hourglass },
  invited:    { label: { fr: 'Invité',      en: 'Invited'    }, variant: 'default',     icon: Mail },
  registered: { label: { fr: 'Inscrit',     en: 'Registered' }, variant: 'default',     icon: CheckCircle2 },
  rejected:   { label: { fr: 'Rejeté',      en: 'Rejected'   }, variant: 'destructive', icon: Ban },
};

const AdminBetaSignupsPage = () => {
  const { language } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, invited: 0, registered: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [notesDialog, setNotesDialog] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const res = await axios.get('/api/admin/beta-signups', { params });
      setItems(res.data.items);
      setCounts(res.data.counts);
    } catch (err) {
      console.error(err);
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, language]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleInvite = async (signup) => {
    try {
      const res = await axios.post(`/api/admin/beta-signups/${signup.id}/invite`);
      toast.success(language === 'fr' ? 'Invitation envoyée' : 'Invitation sent');
      if (res.data?.invitation_url) {
        try { await navigator.clipboard.writeText(res.data.invitation_url); } catch (_) {}
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleResend = async (signup) => {
    try {
      const res = await axios.post(`/api/admin/beta-signups/${signup.id}/resend`);
      toast.success(language === 'fr' ? 'Nouveau lien envoyé' : 'Fresh link sent');
      if (res.data?.invitation_url) {
        try { await navigator.clipboard.writeText(res.data.invitation_url); } catch (_) {}
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    try {
      await axios.post(`/api/admin/beta-signups/${rejectDialog.id}/reject`);
      toast.success(language === 'fr' ? 'Demande rejetée' : 'Request rejected');
      setRejectDialog(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await axios.delete(`/api/admin/beta-signups/${deleteDialog.id}`);
      toast.success(t('success_deleted', language));
      setDeleteDialog(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const saveNotes = async () => {
    if (!notesDialog) return;
    try {
      await axios.patch(`/api/admin/beta-signups/${notesDialog.id}`, { admin_notes: notesValue });
      toast.success(t('success_saved', language));
      setNotesDialog(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const copyInviteLink = async (signup) => {
    const siteUrl = `${window.location.protocol}//${window.location.host}`;
    const url = `${siteUrl}/register?token=${signup.invitation_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(language === 'fr' ? 'Lien copié' : 'Link copied');
    } catch (_) {
      toast.error(language === 'fr' ? 'Copie impossible' : 'Copy failed');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch (_) {
      return iso;
    }
  };

  const isExpired = (iso) => {
    if (!iso) return false;
    try { return new Date(iso).getTime() < Date.now(); } catch (_) { return false; }
  };

  const statusBadge = (status) => {
    const meta = STATUS_META[status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
      <Badge variant={meta.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {meta.label[language] || status}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold font-cinzel">
            {language === 'fr' ? 'Waitlist beta' : 'Beta waitlist'}
          </h1>
          <Button variant="outline" onClick={fetchItems} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
          </Button>
        </div>

        {/* Counters / filters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {['', 'pending', 'invited', 'registered', 'rejected'].map((key) => {
            const isActive = statusFilter === key;
            const count = key === '' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[key] || 0;
            const label = key === ''
              ? (language === 'fr' ? 'Tous' : 'All')
              : STATUS_META[key].label[language];
            return (
              <button
                key={key || 'all'}
                onClick={() => setStatusFilter(key)}
                className={`rounded-md border p-3 text-left transition ${isActive ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}
              >
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
                <div className="text-lg font-semibold">{count}</div>
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('email', language)}</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">{language === 'fr' ? 'Statut' : 'Status'}</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">{language === 'fr' ? 'Demandé le' : 'Requested'}</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">{language === 'fr' ? 'Expiration token' : 'Token expires'}</th>
                    <th className="text-left p-3 font-medium hidden xl:table-cell">IP</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const expired = s.status === 'invited' && isExpired(s.token_expires_at);
                    return (
                      <tr key={s.id} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-medium">{s.email}</span>
                            {s.admin_notes && (
                              <span className="text-xs text-muted-foreground italic truncate max-w-xs">
                                {s.admin_notes}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            {statusBadge(s.status)}
                            {expired && (
                              <Badge variant="destructive" className="gap-1">
                                <Clock className="h-3 w-3" />
                                {language === 'fr' ? 'Expiré' : 'Expired'}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground hidden md:table-cell">{formatDate(s.created_at)}</td>
                        <td className="p-3 text-muted-foreground hidden lg:table-cell">{formatDate(s.token_expires_at)}</td>
                        <td className="p-3 text-muted-foreground hidden xl:table-cell">{s.ip || '—'}</td>
                        <td className="p-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {s.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleInvite(s)}>
                                  <Send className="mr-2 h-4 w-4" />
                                  {language === 'fr' ? 'Inviter (envoyer email)' : 'Invite (send email)'}
                                </DropdownMenuItem>
                              )}
                              {s.status === 'invited' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleResend(s)}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    {language === 'fr' ? 'Régénérer le lien' : 'Resend link'}
                                  </DropdownMenuItem>
                                  {s.invitation_token && (
                                    <DropdownMenuItem onClick={() => copyInviteLink(s)}>
                                      <Copy className="mr-2 h-4 w-4" />
                                      {language === 'fr' ? 'Copier le lien' : 'Copy link'}
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              {s.status === 'rejected' && (
                                <DropdownMenuItem onClick={() => handleInvite(s)}>
                                  <Send className="mr-2 h-4 w-4" />
                                  {language === 'fr' ? 'Réinviter' : 'Re-invite'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => {
                                setNotesValue(s.admin_notes || '');
                                setNotesDialog(s);
                              }}>
                                <StickyNote className="mr-2 h-4 w-4" />
                                {language === 'fr' ? 'Modifier les notes' : 'Edit notes'}
                              </DropdownMenuItem>
                              {s.status !== 'rejected' && s.status !== 'registered' && (
                                <DropdownMenuItem className="text-destructive" onClick={() => setRejectDialog(s)}>
                                  <XCircle className="mr-2 h-4 w-4" />
                                  {language === 'fr' ? 'Rejeter' : 'Reject'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteDialog(s)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                {language === 'fr' ? 'Supprimer' : 'Delete'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-muted-foreground">
                        {t('no_results', language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reject */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Rejeter la demande' : 'Reject request'}</DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? `Marquer la demande de ${rejectDialog?.email} comme rejetée ? Aucun email ne sera envoyé.`
                : `Mark ${rejectDialog?.email}'s request as rejected? No email will be sent.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>{t('cancel', language)}</Button>
            <Button variant="destructive" onClick={handleReject}>{t('confirm', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Supprimer la demande ?' : 'Delete request?'}</DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? `Supprimer définitivement la demande de ${deleteDialog?.email}. Cette action est irréversible.`
                : `Permanently delete the request from ${deleteDialog?.email}. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>{t('cancel', language)}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t('confirm', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes */}
      <Dialog open={!!notesDialog} onOpenChange={(o) => !o && setNotesDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Notes internes' : 'Internal notes'}</DialogTitle>
            <DialogDescription>{notesDialog?.email}</DialogDescription>
          </DialogHeader>
          <Input
            value={notesValue}
            onChange={(e) => setNotesValue(e.target.value)}
            placeholder={language === 'fr' ? 'Ex: contact priorité, partenaire…' : 'E.g. priority contact, partner…'}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialog(null)}>{t('cancel', language)}</Button>
            <Button onClick={saveNotes}>{t('save', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBetaSignupsPage;
